/**
 * TanStack Query Hooks —— 调用 Java 后端 API
 *
 * 用法：
 *   import { useListings } from '@/lib/api/hooks'
 *   const { data, isLoading, error } = useListings({ page: 1, brand: 'Toyota' })
 */
'use client';
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listingApi } from '@/lib/api/listings';
import { homeApi } from '@/lib/api/home';
import { dealerApi } from '@/lib/api/dealer';
import { partsApi } from '@/lib/api/parts';
import { articleApi } from '@/lib/api/articles';
/** 车辆列表查询 Key */
export function listingKeys(params) {
    return ['listings', params !== null && params !== void 0 ? params : {}];
}
/** 列表查询 Hook（TanStack Query + Java 后端） */
export function useListings(params = {}) {
    return useQuery({
        queryKey: listingKeys(params),
        queryFn: () => listingApi.list(params),
        // 当后端未启动时，不会无限重试
        retry: 1,
        retryDelay: 3000,
    });
}
/** 精选车辆 Hook */
export function useFeaturedListings() {
    return useQuery({
        queryKey: ['listings', 'featured'],
        queryFn: () => listingApi.featured(),
        retry: 1,
    });
}
/** 最新车辆 Hook */
export function useLatestListings(limit = 8) {
    return useQuery({
        queryKey: ['listings', 'latest', limit],
        queryFn: () => listingApi.latest(limit),
        retry: 1,
    });
}
/** 车辆详情 Hook */
export function useListingDetail(id) {
    return useQuery({
        queryKey: ['listings', id],
        queryFn: () => listingApi.detail(id),
        enabled: !!id,
        retry: 1,
    });
}
/** 创建车辆 Mutation */
export function useCreateListing() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => listingApi.create(payload),
        onSuccess: () => {
            // 创建成功后刷新列表
            queryClient.invalidateQueries({ queryKey: ['listings'] });
        },
    });
}
/** 删除车辆 Mutation */
export function useDeleteListing() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => listingApi.remove(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['listings'] });
        },
    });
}

export const useGetHomeInfo = () => {
    return useQuery({
        queryKey: ['home', 'homeInfo'],
        queryFn: () => homeApi.homeInfo(),
        retry: 1,
    });
}

export function dealerKeys(params) {
    return ['dealer', params !== null && params !== void 0 ? params : {}];
}

export const useGetDealerList = (params = {}) => {
    return useQuery({
        queryKey: dealerKeys(params),
        queryFn: () => dealerApi.dealerList(params),
        retry: 1,
        retryDelay: 3000
    });
}

export const useGetDealerDetail = (id) => {
    return useQuery({
        queryKey: ['dealer', 'detail', id],
        queryFn: () => dealerApi.dealerDetail(id),
        enabled: !!id,
        retry: 1,
        retryDelay: 3000
    });
}

export const useGetDealerListings = (id) => {
    return useQuery({
        queryKey: ['dealer', 'listings', id],
        queryFn: () => dealerApi.dealerListings(id),
        enabled: !!id,
        retry: 1,
        retryDelay: 3000
    });
}

export const useGetCityList = () => {
    return useQuery({
        queryKey: ['city', 'list'],
        queryFn: () => dealerApi.cityList(),
        retry: 1,
        retryDelay: 3000
    });
}

export const useGetFeaturedParts = (limit = 40) => {
    return useQuery({
        queryKey: ['parts', 'featured', limit],
        queryFn: () => partsApi.featured(limit),
        retry: 1,
        retryDelay: 3000
    });
}

export const useGetCategoryList = (type = 'part') => {
    return useQuery({
        queryKey: ['category', 'list', type],
        queryFn: () => partsApi.categoryList(type),
        retry: 1,
        retryDelay: 3000
    });
}

export const useGetPartList = (params = {}) => {
    return useQuery({
        queryKey: ['parts', 'list', params],
        queryFn: () => partsApi.list(params),
        retry: 1,
        retryDelay: 3000
    });
}

export const useGetPartDetail = (id) => {
    return useQuery({
        queryKey: ['parts', 'detail', id],
        queryFn: () => partsApi.detail(id),
        enabled: !!id,
        retry: 1,
        retryDelay: 3000
    });
}

export const useGetListingList = (params = {}) => {
    return useQuery({
        queryKey: ['listing', 'list', params],
        queryFn: () => listingApi.listingList(params),
        retry: 1,
        retryDelay: 3000
    });
}

export const useGetFilterCounts = () => {
    return useQuery({
        queryKey: ['listing', 'filterCounts'],
        queryFn: () => listingApi.filterCounts(),
        retry: 1,
        retryDelay: 3000
    });
}

export const useGetHotArticles = () => {
    return useQuery({
        queryKey: ['article', 'hot'],
        queryFn: () => articleApi.hot(),
        retry: 1,
        retryDelay: 3000
    });
}

export const useGetArticleCategories = () => {
    return useQuery({
        queryKey: ['article', 'categories'],
        queryFn: () => articleApi.categories(),
        retry: 1,
        retryDelay: 3000
    });
}

export const useGetArticleList = (params = {}) => {
    return useQuery({
        queryKey: ['article', 'list', params],
        queryFn: () => articleApi.list(params),
        retry: 1,
        retryDelay: 3000
    });
}

export const useGetArticleDetail = (id) => {
    return useQuery({
        queryKey: ['article', 'detail', id],
        queryFn: () => articleApi.detail(id),
        enabled: !!id,
        retry: 1,
        retryDelay: 3000
    });
}

/** 获取同分类的上一篇/下一篇文章 */
export const useGetArticleNeighbors = ({ articleId, categoryId }) => {
    return useQuery({
        queryKey: ['article', 'neighbors', { articleId, categoryId }],
        queryFn: async () => {
            // Fetch up to 50 articles from the same category, newest first
            const res = await articleApi.list({ categoryId, sort: 'time', pageNum: 1, pageSize: 50 });
            const list = res?.data ?? res ?? [];
            const arr = Array.isArray(list) ? list : (list.records ?? []);
            const idx = arr.findIndex((a) => String(a.id) === String(articleId));
            if (idx === -1) return { prev: null, next: null };
            // Newest first: next = earlier one (idx+1), prev = newer one (idx-1)
            return {
                prev: idx + 1 < arr.length ? arr[idx + 1] : null,
                next: idx - 1 >= 0 ? arr[idx - 1] : null,
            };
        },
        enabled: !!articleId,
        retry: 1,
        retryDelay: 3000
    });
}

/** 获取数据看板统计（GET /api/listing/mine/stats） */
export function useGetMyStats() {
    return useQuery({
        queryKey: ['listing', 'myStats'],
        queryFn: () => listingApi.myStats(),
        retry: 1,
        retryDelay: 3000,
        // 侧边栏 nav 和 Dashboard 共用同 key，并发请求自动合并为 1 次网络请求
        staleTime: 30 * 1000,
    });
}

/** 获取我的广告列表（分页） */
export function useGetMyListings(params = {}) {
    return useQuery({
        queryKey: ['listing', 'mine', params],
        queryFn: () => listingApi.myListings(params),
        retry: 1,
        retryDelay: 3000
    });
}

/** 获取收藏列表（分页） */
export function useGetFavorites(params = {}) {
    return useQuery({
        queryKey: ['favorite', 'list', params],
        queryFn: () => listingApi.favorites(params),
        retry: 1,
        retryDelay: 3000
    });
}

/** 获取收藏我的车辆的列表（分页，/api/favorite/received） */
export function useGetFavoriteReceived(params = {}) {
    return useQuery({
        queryKey: ['favorite', 'received', params],
        queryFn: () => listingApi.favoriteReceived(params),
        retry: 1,
        retryDelay: 3000
    });
}

/** 获取收藏我的未读列表（/api/favorite/received/unread），enabled=false 时不请求；cacheTime=0 保证每次展开都重新请求 */
export function useGetFavoriteReceivedUnread(params = {}, enabled = true) {
    return useQuery({
        queryKey: ['favorite', 'received', 'unread', params],
        queryFn: () => listingApi.favoriteReceivedUnread(params),
        enabled,
        cacheTime: 0,
        staleTime: 0,
        refetchOnMount: true,
        retry: 1,
        retryDelay: 3000
    });
}

/** 获取收藏我的未读数（/api/favorite/received/unread/count）
 *  默认 enabled=false 不自动请求，由外部（定时器 / 触发式）手动 refetch 控制
 */
export function useGetFavoriteReceivedUnreadCount({ enabled = false } = {}) {
    return useQuery({
        queryKey: ['favorite', 'received', 'unread', 'count'],
        queryFn: () => listingApi.favoriteReceivedUnreadCount(),
        enabled,
        cacheTime: 0,
        staleTime: 0,
        retry: 1,
        retryDelay: 3000
    });
}

/** 收藏/取消收藏 */
export function useToggleFavorite() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (listingId) => listingApi.toggleFavorite(listingId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['listing', 'list'] });
            queryClient.invalidateQueries({ queryKey: ['favorite'] });
        },
    });
}

/** 获取收到的询价列表 */
export function useGetInquiries(params = {}) {
    return useQuery({
        queryKey: ['inquiry', 'received', params],
        queryFn: () => listingApi.inquiries(params),
        retry: 1,
        retryDelay: 3000
    });
}

/** 获取"询价我的车"会话列表（/api/message/conversations/user） */
export function useGetInquiriesUser(params = {}) {
    return useQuery({
        queryKey: ['message', 'conversations', 'user', params],
        queryFn: () => listingApi.inquiriesUser(params),
        retry: 1,
        retryDelay: 3000
    });
}

/** 获取我发出的询价列表（我询价的车辆） */
export function useGetInquiriesSent(params = {}) {
    return useQuery({
        queryKey: ['inquiry', 'sent', params],
        queryFn: () => listingApi.inquiriesSent(params),
        retry: 1,
        retryDelay: 3000
    });
}

/** 推广套餐列表（GET /api/promotion/plans） */
export function useGetPromotionPlans() {
    return useQuery({
        queryKey: ['promotion', 'plans'],
        queryFn: () => listingApi.promotionPlans(),
        retry: 1,
        retryDelay: 3000,
    });
}

/** 推广订单（GET /api/promotion/my-orders） */
export function useGetPromotionMyOrders() {
    return useQuery({
        queryKey: ['promotion', 'my-orders'],
        queryFn: () => listingApi.promotionMyOrders(),
        retry: 1,
        retryDelay: 3000,
    });
}

/**
 * 消息会话列表（/api/message/conversations），enabled=false 不请求；refreshKey 每次变化强制重新请求
 */
export function useMessageConversations({ enabled = true, refreshKey = 0 } = {}) {
    return useQuery({
        queryKey: ['message', 'conversations', refreshKey],
        queryFn: () => listingApi.messageConversations(),
        enabled: enabled && refreshKey > 0,
        cacheTime: 0,
        staleTime: 0,
        retry: 1,
        retryDelay: 3000,
    });
}

/**
 * 对话历史消息（/api/message/conversation）—— 下拉加载更早消息
 *
 * 展示：新消息在底部（index 0），下拉触顶时调用 loadMore 取更早的一页（prepend 到数组前面）
 *
 *   const { list, loading, loadingMore, hasMore, loadMore, reset, sendMessage } = useMessageConversation({
 *     enabled: true, otherUserId: 1002, listingId: 1234, pageSize: 20,
 *   });
 *
 * 注意：refreshKey 每次变化会重置分页并重新请求第一页
 */
export function useMessageConversation({ enabled = false, otherUserId, listingId, pageSize = 20, refreshKey = 0 } = {}) {
    const [pageNum, setPageNum] = useState(1);
    const [allList, setAllList] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const queryClient = useQueryClient();

    // refreshKey / key 参数变化 → 重置
    useEffect(() => {
        setPageNum(1);
        setAllList([]);
        setHasMore(true);
    }, [refreshKey, otherUserId, listingId]);

    const fullKey = ['message', 'conversation', otherUserId, listingId, pageNum, pageSize, refreshKey];

    const query = useQuery({
        queryKey: fullKey,
        queryFn: () => listingApi.messageConversation({ otherUserId, listingId, pageNum, pageSize }),
        enabled: enabled && !!otherUserId && refreshKey > 0,
        cacheTime: 0,
        staleTime: 0,
        retry: 1,
    });

    // 第一页返回 → reverse（后端倒序返回）后 setAllList
    // 后续页 → reverse + prepend，保证 allList 始终 oldest→newest 正序
    useEffect(() => {
        if (!query.data) return;
        const records = query.data?.data?.records
            || query.data?.records
            || Array.isArray(query.data?.data) ? query.data.data
            : Array.isArray(query.data) ? query.data
            : [];
        const list = Array.isArray(records) ? records : [];
        const total = query.data?.pagination?.total ?? query.data?.total ?? null;

        // 后端 records 通常是倒序（latest first），这里统一 reverse 成正序 oldest→newest
        const ordered = list.slice().reverse();

        if (pageNum === 1) {
            setAllList(ordered);
            if (total != null) {
                setHasMore(list.length < total);
            } else {
                setHasMore(list.length >= pageSize);
            }
        } else {
            // 更早的页 prepend 到前面，保持整体 oldest→newest
            setAllList((prev) => [...ordered, ...prev]);
            if (total != null) {
                setHasMore(allList.length + ordered.length < total);
            } else {
                setHasMore(list.length >= pageSize);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.data, pageNum]);

    // 错误时停止 loadingMore
    useEffect(() => {
        if (query.error) setLoadingMore(false);
    }, [query.error]);

    const loadMore = useCallback(() => {
        if (loadingMore || !hasMore || query.isFetching) return;
        setLoadingMore(true);
        setPageNum((p) => p + 1);
        // loadingMore 会在 query.data 变化后由下一个 effect 间接清除，
        // 这里用 setTimeout 兜底防止卡住
        setTimeout(() => setLoadingMore(false), 3000);
    }, [loadingMore, hasMore, query.isFetching]);

    const reset = useCallback(() => {
        setPageNum(1);
        setAllList([]);
        setHasMore(true);
    }, []);

    return {
        list: allList,
        loading: query.isLoading,
        loadingMore: loadingMore || (query.isFetching && pageNum > 1),
        hasMore,
        loadMore,
        reset,
        refetch: query.refetch,
    };
}

/**
 * 发送消息（POST /api/message）
 */
export function useSendMessage() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ receiverId, listingId, content }) =>
            listingApi.sendMessage({ receiverId, listingId, content }),
        onSuccess: () => {
            // 可以 invalidate 相关的 query key，让列表自动刷新
            queryClient.invalidateQueries({ queryKey: ['message', 'conversation'] });
            queryClient.invalidateQueries({ queryKey: ['message', 'conversations'] });
        },
    });
}

/**
 * 通知列表 —— 上拉加载下一页（自管理分页 + 追加，enabled=false 时不请求）
 *
 *   const { list, loading, loadingMore, hasMore, loadMore, reset } = useNotificationList({ enabled: true, pageSize: 10, refreshKey: 0 });
 *
 *   refreshKey 每次变化都会强制重新请求（用于每次打开弹窗都重新取数据）
 *   reset() 可在切换 tab / 关闭弹窗时重置到第一页
 */
export function useNotificationList({ enabled = true, pageSize = 10, refreshKey = 0 } = {}) {
    const [pageNum, setPageNum] = useState(1);
    const [allList, setAllList] = useState([]);
    const [hasMore, setHasMore] = useState(true);

    // refreshKey 变化 → 强制 reset 到第一页
    useEffect(() => {
        setPageNum(1);
        setAllList([]);
        setHasMore(true);
    }, [refreshKey]);

    const query = useQuery({
        queryKey: ['notification', 'list', pageNum, pageSize, refreshKey],
        queryFn: () => listingApi.notificationList({ pageNum, pageSize }),
        enabled: enabled && refreshKey > 0, // refreshKey=0 时不请求，等第一次打开弹窗时+1
        cacheTime: 0,
        staleTime: 0,
        retry: 1,
        retryDelay: 3000,
    });

    // 首次加载 / pageNum 变化时追加到 allList
    useEffect(() => {
        if (enabled && query.data) {
            const records = query.data?.data?.records
                || query.data?.records
                || query.data?.data
                || [];
            const arr = Array.isArray(records) ? records : [];
            if (pageNum === 1) {
                setAllList(arr);
            } else {
                setAllList((prev) => [...prev, ...arr]);
            }
            // 判断是否还有更多
            const pagination = query.data?.data?.pagination || query.data?.pagination;
            const total = pagination?.total ?? query.data?.total ?? 0;
            if (total > 0) {
                setHasMore(pageNum * pageSize < total);
            } else {
                setHasMore(arr.length >= pageSize); // 没有 total 时，看本页是否满
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.data, enabled]);

    const loadMore = () => {
        if (!query.isLoading && hasMore) {
            setPageNum((p) => p + 1);
        }
    };

    const reset = () => {
        setPageNum(1);
        setAllList([]);
        setHasMore(true);
    };

    return {
        list: allList,
        loading: query.isLoading && pageNum === 1,
        loadingMore: query.isLoading && pageNum > 1,
        hasMore,
        loadMore,
        reset,
        error: query.error,
    };
}

/**
 * 通用卡片收藏 hook（首页精选/EV/最新列表等复用，逻辑与搜索页完全一致）
 *
 * 用法：
 *   const { favoritedIds, handleFavorite, toastNode } = useCardFavorites(records);
 *   // JSX 中：
 *   // {toastNode}
 *   // <button onClick={(e) => handleFavorite(e, car.id)} ...>
 */
export function useCardFavorites(records = []) {
    const [favoritedIds, setFavoritedIds] = useState(new Set());
    const [toast, setToast] = useState(null);
    const toggleFavMutation = useToggleFavorite();

    useEffect(() => {
        if (records && records.length > 0) {
            setFavoritedIds(new Set(records.filter((r) => r.isFavorite === 1).map((r) => r.id)));
        }
    }, [records]);

    const showToast = useCallback((msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    }, []);

    const handleFavorite = useCallback(async (e, listingId) => {
        e.preventDefault();
        e.stopPropagation();
        const wasFavorited = favoritedIds.has(listingId);
        setFavoritedIds((prev) => {
            const next = new Set(prev);
            if (wasFavorited) next.delete(listingId); else next.add(listingId);
            return next;
        });
        try {
            await toggleFavMutation.mutateAsync(listingId);
            showToast(wasFavorited ? "取消收藏成功" : "收藏成功");
        } catch (err) {
            console.error('[favorite] toggle error:', err);
            setFavoritedIds((prev) => {
                const next = new Set(prev);
                if (wasFavorited) next.add(listingId); else next.delete(listingId);
                return next;
            });
            showToast(err?.message || "操作失败，请稍后重试", "error");
        }
    }, [favoritedIds, toggleFavMutation, showToast]);

    const toastNode = (toast && typeof document !== 'undefined')
        ? createPortal(
            <div style={{
                position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 9999,
                padding: "10px 24px", borderRadius: 8, fontSize: 14, fontWeight: 500,
                background: toast.type === "success" ? "#53c906" : "#EF4444",
                color: "#fff", boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}>
                {toast.msg}
            </div>,
            document.body
        )
        : null;

    return { favoritedIds, handleFavorite, toastNode };
}
